import { loginWithGoogle, loginWithEmail, signupWithEmail } from './FirebaseAuth';

export class AuthModal {
    private backdrop: HTMLDivElement;
    private card: HTMLDivElement;
    private mode: 'signin' | 'signup' = 'signin';
    private emailInput!: HTMLInputElement;
    private passInput!: HTMLInputElement;
    private passToggleBtn!: HTMLButtonElement;
    private submitBtn!: HTMLButtonElement;
    private headerTitle!: HTMLHeadingElement;
    private subtitle!: HTMLParagraphElement;
    private toggleModeBtn!: HTMLButtonElement;
    private toggleModeText!: HTMLSpanElement;

    constructor(
        private options: {
            onClose: () => void;
            onSuccess: () => void;
        }
    ) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'modal-backdrop';
        this.backdrop.onclick = (e) => {
            if (e.target === this.backdrop) this.options.onClose();
        };

        this.card = document.createElement('div');
        this.card.className = 'modal-card';
        // Prevent click from bubbling to backdrop
        this.card.onclick = (e) => e.stopPropagation();
        this.backdrop.appendChild(this.card);

        this.handleEsc = this.handleEsc.bind(this);
        document.addEventListener('keydown', this.handleEsc);

        this.buildDOM();
        this.updateText();
    }

    private buildDOM() {
        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal-close-x';
        closeBtn.onclick = this.options.onClose;
        closeBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
        this.card.appendChild(closeBtn);

        this.headerTitle = document.createElement('h2');
        this.headerTitle.className = 'modal-header';
        this.card.appendChild(this.headerTitle);

        this.subtitle = document.createElement('p');
        this.subtitle.className = 'auth-subtitle';
        this.card.appendChild(this.subtitle);

        const form = document.createElement('form');
        form.className = 'auth-form';
        form.onsubmit = this.handleSubmit.bind(this);

        this.emailInput = document.createElement('input');
        this.emailInput.type = 'email';
        this.emailInput.className = 'auth-input';
        this.emailInput.placeholder = 'Email address';
        this.emailInput.autocomplete = 'username';
        this.emailInput.required = true;
        form.appendChild(this.emailInput);

        const passContainer = document.createElement('div');
        passContainer.className = 'pass-container';

        this.passInput = document.createElement('input');
        this.passInput.type = 'password';
        this.passInput.className = 'auth-input pass-input';
        this.passInput.placeholder = 'Password';
        this.passInput.required = true;
        passContainer.appendChild(this.passInput);

        this.passToggleBtn = document.createElement('button');
        this.passToggleBtn.type = 'button';
        this.passToggleBtn.className = 'pass-toggle';
        this.passToggleBtn.title = 'Toggle password visibility';
        this.passToggleBtn.onclick = this.togglePasswordVisibility.bind(this);
        this.updatePassToggleIcon();
        passContainer.appendChild(this.passToggleBtn);

        form.appendChild(passContainer);

        this.submitBtn = document.createElement('button');
        this.submitBtn.type = 'submit';
        this.submitBtn.className = 'modal-btn btn-gradient';
        form.appendChild(this.submitBtn);

        this.card.appendChild(form);

        const footer = document.createElement('div');
        footer.className = 'auth-footer';

        this.toggleModeText = document.createElement('span');
        this.toggleModeText.style.color = 'var(--text-dim)';
        footer.appendChild(this.toggleModeText);

        this.toggleModeBtn = document.createElement('button');
        this.toggleModeBtn.className = 'auth-toggle-btn';
        this.toggleModeBtn.type = 'button';
        this.toggleModeBtn.onclick = () => {
            this.mode = this.mode === 'signin' ? 'signup' : 'signin';
            this.updateText();
        };
        footer.appendChild(this.toggleModeBtn);

        this.card.appendChild(footer);

        const divider = document.createElement('div');
        divider.className = 'auth-divider';
        const line1 = document.createElement('div');
        line1.className = 'auth-divider-line';
        const text = document.createElement('span');
        text.className = 'auth-divider-text';
        text.textContent = 'OR';
        const line2 = document.createElement('div');
        line2.className = 'auth-divider-line';

        divider.appendChild(line1);
        divider.appendChild(text);
        divider.appendChild(line2);
        this.card.appendChild(divider);

        const googleBtn = document.createElement('button');
        googleBtn.className = 'modal-btn btn-google';
        googleBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg> Continue with Google`;
        googleBtn.onclick = async () => {
            try {
                await loginWithGoogle();
                this.options.onSuccess();
            } catch (error) {
                console.error(error);
            }
        };
        this.card.appendChild(googleBtn);
    }

    private updateText() {
        const isSignIn = this.mode === 'signin';
        this.headerTitle.textContent = isSignIn ? 'Sign In' : 'Create Account';
        this.subtitle.textContent = isSignIn ? 'Welcome back! Sign in to access your sprites.' : 'Sign up to start saving your sprites to the cloud.';

        this.passInput.autocomplete = isSignIn ? 'current-password' : 'new-password';
        this.submitBtn.textContent = isSignIn ? 'Sign In' : 'Create Account';

        this.toggleModeText.textContent = isSignIn ? "Don't have an account? " : "Already have an account? ";
        this.toggleModeBtn.textContent = isSignIn ? 'Create Account' : 'Sign In';
    }

    private togglePasswordVisibility() {
        const isVisible = this.passInput.type === 'text';
        this.passInput.type = isVisible ? 'password' : 'text';
        this.updatePassToggleIcon();
    }

    private updatePassToggleIcon() {
        const isVisible = this.passInput.type === 'text';
        const eyeOn = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>`;
        const eyeOff = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>`;
        this.passToggleBtn.innerHTML = isVisible ? eyeOff : eyeOn;
    }

    private async handleSubmit(e: SubmitEvent) {
        e.preventDefault();
        const email = this.emailInput.value;
        const password = this.passInput.value;

        this.submitBtn.disabled = true;
        this.submitBtn.style.opacity = '0.7';
        this.submitBtn.textContent = 'Processing...';

        try {
            if (this.mode === 'signin') {
                await loginWithEmail(email, password);
            } else {
                await signupWithEmail(email, password);
            }
            this.options.onSuccess();
        } catch (error: any) {
            console.error(error);
            let msg = error.message || 'Authentication failed';
            alert(msg);
            this.submitBtn.disabled = false;
            this.submitBtn.style.opacity = '1';
            this.submitBtn.textContent = this.mode === 'signin' ? 'Sign In' : 'Create Account';
        }
    }

    private handleEsc(e: KeyboardEvent) {
        if (e.key === 'Escape') this.options.onClose();
    }

    public getElement() {
        return this.backdrop;
    }

    public destroy() {
        document.removeEventListener('keydown', this.handleEsc);
        this.backdrop.remove();
    }
}
